import React, { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "../common/SelectInput";
import { NewIntegration, DestinationType, Organization } from "../common/ObjectTypes";
import { AttentionIcon } from "../common/ProjectIcons";
import { checkSchema, isActionVisible } from "../common/SchemaFormTemplates/checkSchema";
import { filterConfigurableActions } from "../common/SchemaFormTemplates/actionFilters";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import { isEarthRangerDestinationType } from "./earthRangerUrl";
import EarthRangerSiteInput from "./EarthRangerSiteInput";
import { getButtonText } from "../common/TestActionFunctions";
import { Tooltip } from "../common/Tooltip";
import { buttonClasses } from "../common/tailwindClassnames";
import { useHelp, HelpCard } from "../../context/HelpContext";
import SelectionPanel from "../common/configPanel/SelectionPanel";
import ActionSectionForm from "../common/configPanel/ActionSectionForm";
import { DETAILS_SLUG, actionForSlug, buildActionNav } from "../common/configPanel/buildActionNav";

type DestinationModalProps = {
    currentMemberIsAdmin: boolean,
    errors: any,
    rsjfErrors: any,
    onToggleModal: MouseEventHandler,
    destination: NewIntegration,
    destinationType: DestinationType,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    orgSearchActive?: boolean,
    destinationTypes: DestinationType[],
    // True when this modal is creating a destination for a connection: only the
    // receiving capability is relevant, so the list is flat and push-only.
    receiveOnly?: boolean,
    onChange: Function,
    onChangeOrg: Function,
    onChangeType: Function,
    onChangeJSONForm: Function,
    // The configurations typed so far, so a section that was navigated away from
    // comes back with its values instead of an empty form.
    configurations: any[],
    onSave: (e: React.FormEvent<Element>, action?: "save" | "saveAndClose") => void,
    onGoToDetails: (destination_id: string) => void,
    changesMade: boolean,
}

const DestinationModal = ({
    currentMemberIsAdmin,
    errors,
    rsjfErrors,
    onToggleModal,
    destination,
    destinationType,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    orgSearchActive = false,
    destinationTypes,
    receiveOnly = false,
    onChange,
    onChangeOrg,
    onChangeType,
    onChangeJSONForm,
    configurations,
    onSave,
    onGoToDetails,
    changesMade,
}: DestinationModalProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();
    const [activeSlug, setActiveSlug] = useState<string>(DETAILS_SLUG);
    // This technology derives its address from its configuration, so a URL is
    // editable but not required.
    const urlIsOptional = isHiddenUrlDestinationType(destinationType.name);
    const isEarthRanger = isEarthRangerDestinationType(destinationType.name);

    // Track if a type has been selected
    const typeSelected = destinationType && destinationType.id && destinationType.id !== '0';

    useEffect(() => {
        if (!typeSelected) {
            setHelpContent(null);
            return;
        }
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Connected System in Gundi'),
                description: t('Find step-by-step instructions for connecting a system to Gundi, as a source or a destination.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/destinations' },
            },
            ...(destinationType.help_center_url ? [{
                title: t('{{technology}} Destination Set Up', { technology: destinationType.name }),
                description: t('Configuration guide specific to {{technology}} destinations.', { technology: destinationType.name }),
                button: { label: t('Learn More'), url: destinationType.help_center_url },
            }] : []),
        ];
        setHelpContent({ cards });
    }, [typeSelected, destinationType, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    // A record's role comes from the connections it joins, so every technology is
    // offered. The two groups say what each one is capable of; a technology that can
    // do both appears in both.
    const typeOption = (type: DestinationType) => ({
        label: type.name,
        name: type.name,
        description: type.description,
        id: type.id,
        value: type.id,
        actions: type.actions,
        help_center_url: type.help_center_url,
    });
    const hasAction = (type: DestinationType, kind: string) => (type.actions || []).some((a: any) => a.type === kind);
    // Creating a destination for a connection, only one capability is on offer —
    // grouping by capability would list the same technologies twice under a heading
    // that says nothing.
    const typeOptions = receiveOnly
        ? destinationTypes.filter((type) => hasAction(type, "push")).map(typeOption)
        : [
            { label: t("Can send data to Gundi"), options: destinationTypes.filter((type) => hasAction(type, "pull")).map(typeOption) },
            { label: t("Can receive data from Gundi"), options: destinationTypes.filter((type) => hasAction(type, "push")).map(typeOption) },
        ].filter((group) => group.options.length > 0);

    // Only actions the user can fill in become sections.
    const actions = useMemo(
        () => filterConfigurableActions(destinationType.actions, "pull")
            .filter((a: any) => isActionVisible(a) && checkSchema(a)),
        [destinationType.actions]);
    const nav = buildActionNav({ actions, t });
    const activeAction = actionForSlug(actions, activeSlug);
    const onDetails = activeSlug === DETAILS_SLUG || !activeAction;
    const dataFor = (action: any) => configurations.find((c: any) => c.action === action.id)?.data ?? {};

    const detailsSection = (
        <div className="rounded border border-gray-lines bg-white p-5 transition-colors duration-300">
            {/* Name */}
            <div className="mb-[8px] border-b border-divider-lines pt-1 pb-[8px] transition-all duration-300 w-full">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                    <div className="md:w-1/2 flex flex-col">
                        <label className="mr-1">{t("Name")} *</label>
                        <span className="text-sm text-secondary-gray mt-1">{t("A descriptive name for this connected system.")}</span>
                    </div>
                    <div className="md:w-1/2">
                        <input
                            id="name"
                            placeholder=""
                            className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                            value={destination.name || ""}
                            onChange={(e) => onChange(e)}>
                        </input>
                        {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                    </div>
                </div>
            </div>
            {/* Workspace */}
            <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                    <div className="md:w-1/2 flex flex-col">
                        <label className="mr-1">{t("Workspace")} *</label>
                        <span className="text-sm text-secondary-gray mt-1">{t("The workspace this connected system belongs to.")}</span>
                    </div>
                    <div className="md:w-1/2">
                        <SelectInput
                            options={organizations.map((org: Organization) => ({ label: org.name, value: org.id }))}
                            className={"basic-single text-black"}
                            autoSelectSingleOption={!orgSearchActive}
                            isDisabled={false}
                            isLoading={false}
                            isClearable={false}
                            isSearchable={true}
                            isMulti={false}
                            name={t("Workspace")}
                            onChange={(e: any) => onChangeOrg(e)}
                            onMenuScrollToBottom={loadMoreOrgs}
                            handleInputChange={orgInputChange}
                        />
                        {errors.owner && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                    </div>
                </div>
            </div>
            {/* URL — shown for every technology, the same as on the connected-system
                screen. The ones that derive their address from their configuration
                simply don't require a value. */}
            <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                    <div className="md:w-1/2 flex flex-col">
                        <label className="mr-1">{isEarthRanger ? t("Site URL") : t("URL")}{urlIsOptional ? "" : " *"}</label>
                        <span className="text-sm text-secondary-gray mt-1">{isEarthRanger
                            ? t("The name of the EarthRanger site (e.g. my-site).")
                            : urlIsOptional
                                ? t("This technology derives its address from its configuration. Set a URL only if you need to override it.")
                                : t("The base URL of this connected system.")}</span>
                    </div>
                    <div className="md:w-1/2">
                        {isEarthRanger ? (
                            <EarthRangerSiteInput
                                value={destination.base_url || ""}
                                onChange={(e) => onChange(e)}
                                error={errors.base_url}
                            />
                        ) : (
                            <>
                                <input
                                    id="base_url"
                                    placeholder=""
                                    className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                    value={destination.base_url || ""}
                                    onChange={(e) => onChange(e)}>
                                </input>
                                {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="z-20 flex min-h-screen bg-very-light-gray transition-colors duration-300">
            <div className="flex flex-col w-full min-h-screen my-4">
                <div className="pb-6">
                    <div>
                        <div className="max-w-list-table mx-auto">
                            <h1 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-8 transition-colors duration-300">{receiveOnly ? t("New Destination") : t("New Connected System")}</h1>
                        </div>
                        <div className="flex flex-col justify-between h-screen">
                            <div className="flex flex-row justify-center max-w-list-table mx-auto w-full">
                                <div className="w-full pb-8">
                                    {/* STEP 1: DESTINATION TYPE */}
                                    <div className="px-4 sm:px-6">
                                        <div className="flex flex-col min-w-[400px]">
                                            <label>{t("Type")}</label>
                                            <SelectInput
                                                options={typeOptions}
                                                className={"basic-single text-black"}
                                                defaultValue={{
                                                    label: "Select",
                                                    value: 0
                                                }}
                                                isDisabled={false}
                                                isLoading={false}
                                                isClearable={false}
                                                isSearchable={true}
                                                isMulti={false}
                                                name={t("Type")}
                                                onChange={(e: any) => onChangeType(e)}
                                            />
                                        </div>
                                    </div>

                                    {/* STEP 2: NAME, CONFIGURATION, ENABLED */}
                                    {/* Same shell as a connection's configuration
                                        panel: nav on the left, one section at a time.
                                        Everything here is committed by one submit —
                                        the destination doesn't exist yet, so there is
                                        nothing to save section by section. */}
                                    {typeSelected && (
                                        <div className="px-4 sm:px-6 mt-8 mb-8">
                                            <SelectionPanel
                                                title={t("Configuration")}
                                                nav={nav}
                                                activeSlug={onDetails ? DETAILS_SLUG : activeSlug}
                                                onSelectSection={setActiveSlug}>
                                                {onDetails ? detailsSection : (
                                                    <ActionSectionForm
                                                        target={{ id: "", actions: destinationType.actions || [], configurations: [] }}
                                                        action={activeAction}
                                                        userIsAdmin={currentMemberIsAdmin}
                                                        formData={dataFor(activeAction)}
                                                        errors={rsjfErrors[activeAction.id]}
                                                        onChange={(data: any, fieldId?: string) => onChangeJSONForm(data, activeAction.id, fieldId)}
                                                        showSetupState={false}
                                                        headerActions={activeAction.schema?.is_executable === true ? (
                                                            <div className="flex flex-row items-center">
                                                                <button disabled className={`${buttonClasses.lightGreen} mr-3`}>
                                                                    {getButtonText(activeAction.value, t)}
                                                                </button>
                                                                <Tooltip description={t("Save your destination first to enable this feature.")} position="right" />
                                                            </div>
                                                        ) : undefined}
                                                    />
                                                )}
                                            </SelectionPanel>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* submit and cancel button */}
                            <div className="sticky bottom-0 left-0 bg-white/50 backdrop-blur-sm py-3 w-full transition-colors duration-300 z-50">
                                <div className="max-w-list-table mx-auto flex flex-row gap-3 px-4 sm:px-6">
                                    <button className={buttonClasses.primary}
                                        type="submit"
                                        disabled={!typeSelected || !changesMade}
                                        aria-disabled={!typeSelected || !changesMade}
                                        onClick={(e: any) => onSave(e, "save")}>
                                        {t("Create connected system")}
                                    </button>
                                    <button className={buttonClasses.cancel}
                                        type="button"
                                        onClick={onToggleModal}>
                                        {t("Cancel")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DestinationModal;